import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { Link } from 'components/ui/Link'
import { Sizes } from 'enums/sizes'
import React, { forwardRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'

const styles = {
  base: [
    // Base
    'relative isolate inline-flex items-center justify-center gap-x-2 border text-base/6 font-semibold',
    // Sizing (using Tailwind classes for cross-browser compatibility)
    'px-3 py-2 sm:px-2.5 sm:py-1 sm:text-sm/6',
    // Focus
    'focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-primary',
    // Disabled
    'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
    // Icon
    '[&>[data-slot=icon]]:-mx-0.5 [&>[data-slot=icon]]:my-0.5 [&>[data-slot=icon]]:size-5 [&>[data-slot=icon]]:shrink-0 [&>[data-slot=icon]]:text-[--btn-icon] [&>[data-slot=icon]]:sm:my-1 [&>[data-slot=icon]]:sm:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-[hover]:[--btn-icon:ButtonText]'
  ],

  solidRound: [
    // Optical border, implemented as the button background to avoid corner artifacts
    'border-transparent bg-[--btn-border]',
    // Dark mode: border is rendered on `after` so background is set to button background
    'dark:bg-[--btn-bg]',
    // Button background, implemented as foreground layer to stack on top of pseudo-border layer
    'before:absolute before:inset-0 before:-z-10 before:rounded-md before:bg-[--btn-bg]',
    // Drop shadow, applied to the inset `before` layer so it blends with the border
    'before:shadow',
    // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
    'dark:before:hidden',
    // Dark mode: Subtle white outline is applied using a border
    'dark:border-white/5',
    // Inner highlight shadow
    'after:shadow-[shadow:inset_0_1px_theme(colors.white/15%)]',
    // Disabled
    'before:data-[disabled]:shadow-none after:data-[disabled]:shadow-none',
    // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
    'after:absolute after:inset-0 after:-z-10 after:rounded-full',
    // White overlay on hover
    'after:data-[active]:bg-[--btn-hover-overlay] after:data-[hover]:bg-[--btn-hover-overlay]',
  ],
  solid: [
    // Optical border, implemented as the button background to avoid corner artifacts
    'border-transparent bg-[--btn-border]',
    // Dark mode: border is rendered on `after` so background is set to button background
    'dark:bg-[--btn-bg]',
    // Button background, implemented as foreground layer to stack on top of pseudo-border layer
    'before:absolute before:inset-0 before:-z-10 before:rounded-md before:bg-[--btn-bg]',
    // Drop shadow, applied to the inset `before` layer so it blends with the border
    'before:shadow',
    // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
    'dark:before:hidden',
    // Dark mode: Subtle white outline is applied using a border
    'dark:border-white/5',
    // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
    'after:absolute after:inset-0 after:-z-10 after:rounded-md',
    // Inner highlight shadow
    'after:shadow-[shadow:inset_0_1px_theme(colors.white/15%)]',
    // White overlay on hover
    'after:data-[active]:bg-[--btn-hover-overlay] after:data-[hover]:bg-[--btn-hover-overlay]',
    // Disabled
    'before:data-[disabled]:shadow-none after:data-[disabled]:shadow-none'
  ],
  outline: [
    // Base (default gray style)
    'border-zinc-950/10 text-zinc-950 data-[active]:bg-zinc-950/[2.5%] data-[hover]:bg-zinc-950/[2.5%]',
    // Icon
    '[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.600)] data-[hover]:[--btn-icon:theme(colors.zinc.600)] '
  ],
  outlinePrimary: [
    // Primary color outline
    '!border-primary !text-primary data-[active]:bg-primary/5 data-[hover]:bg-primary/5',
    '[--btn-icon:var(--primary-color)] data-[active]:[--btn-icon:var(--primary-color)] data-[hover]:[--btn-icon:var(--primary-color)]'
  ],
  plain: [
    // Base
    'border-transparent text-zinc-950 data-[active]:bg-zinc-950/5 data-[hover]:bg-zinc-950/5',
    // Icon
    '[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.600)] data-[hover]:[--btn-icon:theme(colors.zinc.600)]'
  ],
  sizes: {
    [Sizes.lg]: [
      'border pt-[13px] pb-[15px] px-[50px]',
      'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
      'text-[14px]',
      'rounded-[5px]',
      'relative isolate inline-flex items-center justify-center gap-x-2 border text-base/6 font-semibold',
    ]
  },
  colors: {
    'dark/zinc': [
      'text-white [--btn-bg:theme(colors.zinc.900)] [--btn-border:theme(colors.zinc.950/90%)] [--btn-hover-overlay:theme(colors.white/10%)]',
      '[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)]'
    ],
    light: [
      'text-zinc-950 [--btn-bg:white] [--btn-border:theme(colors.zinc.950/10%)] [--btn-hover-overlay:theme(colors.zinc.950/2.5%)] data-[active]:[--btn-border:theme(colors.zinc.950/15%)] data-[hover]:[--btn-border:theme(colors.zinc.950/15%)]',
      '[--btn-icon:theme(colors.zinc.500)] data-[active]:[--btn-icon:theme(colors.zinc.700)] data-[hover]:[--btn-icon:theme(colors.zinc.700)]'
    ],
    'dark/white': [
      'text-white [--btn-bg:theme(colors.zinc.900)] [--btn-border:theme(colors.zinc.950/90%)] [--btn-hover-overlay:theme(colors.white/10%)]',
      '[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)]'
    ],
    dark: [
      'text-white [--btn-bg:theme(colors.zinc.900)] [--btn-border:theme(colors.zinc.950/90%)] [--btn-hover-overlay:theme(colors.white/10%)]',
      '[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)]'
    ],
    white: [
      'text-zinc-950 [--btn-bg:white] [--btn-border:theme(colors.zinc.950/10%)] [--btn-hover-overlay:theme(colors.zinc.950/2.5%)] data-[active]:[--btn-border:theme(colors.zinc.950/15%)] data-[hover]:[--btn-border:theme(colors.zinc.950/15%)]',
      '[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.500)] data-[hover]:[--btn-icon:theme(colors.zinc.500)]'
    ],
    primary: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.primary)] [--btn-border:theme(colors.primary/90%)]',
      '[--btn-icon:theme(colors.white)] data-[active]:[--btn-icon:theme(colors.primary)] data-[hover]:[--btn-icon:theme(colors.primary/90%)]'
    ],
    primaryLight: [
      'text-primary [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.gray.300/20%)] [--btn-border:theme(colors.primary/90%)]',
      '[--btn-icon:theme(colors.primary)] data-[active]:[--btn-icon:theme(colors.primary)] data-[hover]:[--btn-icon:theme(colors.primary)]'
    ],
    secondary: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.secondary)] [--btn-border:theme(colors.secondary/90%)]',
      '[--btn-icon:theme(colors.white)] data-[active]:[--btn-icon:theme(colors.secondary)] data-[hover]:[--btn-icon:theme(colors.white)]',
      'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
    ],
    slate: [
      'text-zinc-900 [--btn-hover-overlay:theme(colors.slate.200)] [--btn-bg:theme(colors.slate.100)] [--btn-border:theme(colors.slate.200/90%)]',
      '[--btn-icon:theme(colors.zinc.900)] data-[active]:[--btn-icon:theme(colors.zinc.800)] data-[hover]:[--btn-icon:theme(colors.zinc.700)]'
    ],
    red: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.red.600)] [--btn-border:theme(colors.red.700/90%)]',
      '[--btn-icon:theme(colors.red.300)] data-[active]:[--btn-icon:theme(colors.red.200)] data-[hover]:[--btn-icon:theme(colors.red.200)]'
    ],
    orange: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.orange.500)] [--btn-border:theme(colors.orange.600/90%)]',
      '[--btn-icon:theme(colors.white)] data-[active]:[--btn-icon:theme(colors.orange.100)] data-[hover]:[--btn-icon:theme(colors.orange.100)]'
    ],
  }
}

type ButtonProps = (
  | { color?: keyof typeof styles.colors; outline?: never; plain?: never }
  | { color?: 'primary'; outline: true; plain?: never }
  | { color?: never; outline: true; plain?: never }
  | { color?: never; outline?: never; plain: true }
) & {
  ref: any
  className?: string
  children: React.ReactNode
  round?: boolean
  size?: Sizes.lg
  tooltip?: string
  tooltipPosition?: 'top' | 'bottom'
} & (
    | Omit<Headless.ButtonProps, 'as' | 'className'>
    | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>
  )

export const Button = forwardRef(function Button(
  { color, outline, plain, round = false, size, className, children, tooltip, tooltipPosition: tooltipPos = 'top', ...props }: ButtonProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipCoords, setTooltipCoords] = useState({ top: 0, bottom: 0, left: 0 })
  const buttonRef = React.useRef<HTMLElement>(null)

  // ✅ Utilise useImperativeHandle avec un tableau de dépendances VIDE
  React.useImperativeHandle(ref, () => buttonRef.current as HTMLElement, [])

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    if (tooltip && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setTooltipCoords({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left + rect.width / 2
      })
      setShowTooltip(true)
    }
  }, [tooltip])

  const handleMouseLeave = useCallback(() => {
    if (tooltip) {
      setShowTooltip(false)
    }
  }, [tooltip])

  const classes = clsx(
    className,
    round ? 'rounded-full' : 'rounded-lg',
    !size && styles.base,
    size && styles.sizes[size],
    outline
      ? clsx(
          styles.outline,
          color === 'primary' && styles.outlinePrimary
        )
      : plain
        ? styles.plain
        : clsx(
          round ? styles.solidRound : styles.solid,
          styles.colors[color ?? 'dark/zinc']
        )
  )

  const buttonContent = <TouchTarget>{children}</TouchTarget>

  const tooltipHandlers = tooltip ? {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  } : {}

  const tooltipPortal = tooltip && showTooltip && createPortal(
    <div
      className={clsx(
        "fixed z-[9999] px-3 py-2 bg-white border border-gray-200 text-primary/80 text-sm rounded-lg shadow-lg whitespace-nowrap pointer-events-none animate-in fade-in duration-200",
        tooltipPos === 'top' ? 'slide-in-from-bottom-2' : 'slide-in-from-top-2'
      )}
      style={tooltipPos === 'top' ? {
        top: `${tooltipCoords.top - 8}px`,
        left: `${tooltipCoords.left}px`,
        transform: 'translate(-50%, -100%)'
      } : {
        top: `${tooltipCoords.bottom + 8}px`,
        left: `${tooltipCoords.left}px`,
        transform: 'translate(-50%, 0)'
      }}
    >
      {tooltip}
      {/* Arrow pointing up (for bottom tooltip) or down (for top tooltip) */}
      {tooltipPos === 'top' ? (
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 border-b border-r border-gray-200 bg-white rotate-45"></div>
      ) : (
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 border-t border-l border-gray-200 bg-white rotate-45"></div>
      )}
    </div>,
    document.body
  )

  return (
    <>
      {'href' in props ? (
        <Link
          {...props}
          {...tooltipHandlers}
          className={classes}
          ref={buttonRef as React.ForwardedRef<HTMLAnchorElement>}
        >
          {buttonContent}
        </Link>
      ) : (
        <Headless.Button
          {...props}
          {...tooltipHandlers}
          className={clsx(classes, 'cursor-pointer')}
          ref={buttonRef as React.ForwardedRef<HTMLButtonElement>}
        >
          {buttonContent}
        </Headless.Button>
      )}
      {tooltipPortal}
    </>
  )
})

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span
        className="absolute left-1/2 top-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  )
}
